/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-magic-numbers

import { Directive } from '@angular/core';
import { DateTimeTypes, State } from '@gh/common-shared';
import { currentDate, diffDays, normalizeDate, setHours } from '@gh/core-util';
import { clamp, constant, padStart } from 'lodash';
import {
  GanttState,
  queryGanttCalendar,
  queryGanttEndVisibleDate,
  queryGanttIsAllDaysPeriodType,
  queryGanttStartVisibleDate,
} from '../gantt-chart.state';
import { WORK_TIME_HOURS_END, WORK_TIME_HOURS_START } from '../resources/gantt.model';
import {
  GANTT_CHART_ACCESSOR,
  GANTT_CHART_CONTROLLER,
  GanttChartAccessor,
  GanttChartController,
  GanttChartTimelineDirective,
  GanttChartView,
} from './gantt-chart-timeline.directive';

const dateOfMonthFormatter = (date: Date) => padStart(date.getDate().toString(), 2, '0');

const getPrimaryScaleCellClass = constant('gantt_cell_separator');

const dayOffByYearIndex: Hash<Hash<Hash<boolean>>> = {} = {};

const isDayOff = (date: Date): boolean =>  {
  const byYear = dayOffByYearIndex[date.getFullYear()];
  const byMonth = byYear ? byYear[date.getMonth()] : void 0;
  return byMonth && byMonth[date.getDate()] || false;
};

const  getTaskCellClass = (date: Date): string  => {
  if (-1 !== [0, 6].indexOf(date.getDay())) {
    return 'gantt_cell_weekend';
  } else if (isDayOff(date)) {
    return 'gantt_cell_dayoff';
  } else {
    return '';
  }
};

const getSubScaleCellClass = (date: Date) => {
  const separatorClass = 'gantt_cell_separator';
  const datOffClass = getTaskCellClass(date);
  const day = date.getDay();
  const separator = 1 === day ? separatorClass : '';
  return ` ${separator} ${datOffClass}` ;
};

@Directive({
  selector: '[ghGanttChartTimelineInstance]',
  providers: [
    { provide: GANTT_CHART_ACCESSOR, useExisting: GanttChartTimelineInstanceDirective, multi: true },
    { provide: GANTT_CHART_CONTROLLER, useExisting: GanttChartTimelineInstanceDirective },
  ],
})
export class GanttChartTimelineInstanceDirective implements GanttChartAccessor, GanttChartController {
  minScale: number;
  maxScale: number;

  private gantt: GanttStatic;

  constructor(private state: State<GanttState>,
              private timeline: GanttChartTimelineDirective) {
  }

  onGanttConfig(gantt: GanttStatic): void {
    this.gantt = gantt;

    gantt.config.scale_height = 110;
    this.setupViewOptions(GanttChartView.MONTH, 1);
    this.setupCellClassTemplate();
  }

  onGanttDestroy(gantt: GanttStatic): void {
  }

  onGanttReady(gantt: GanttStatic): void {
    const calendar = this.state.project(queryGanttCalendar);

    if (this.state.project(queryGanttIsAllDaysPeriodType)) {
      this.timeline.setAllWorkingDays();
    } else {
      gantt.setWorkTime({
        hours: [8, 16],
        days: [0, 1, 1, 1, 1, 1, 0],
      });

      calendar.forEach(({ date }) => {
        gantt.setWorkTime({ date, hours: false });
        this.setDayOff(date);
      });
    }

    this.setupCellClassTemplate();
    this.timeline.setupTooltipTemplate([
      { field: 'duration', title: 'lbl_duration' },
      { field: 'startDate', title: 'lbl_start_date' },
      { field: 'endDate', title: 'lbl_end_date' },
      { field: 'progress', title: 'lbl_progress_pct' },
      { field: 'description', title: 'lbl_description' },
    ]);
  }

  setView(mode: GanttChartView, scale: number = 1): void {
    this.setupViewOptions(mode, scale);
    this.gantt.render();
  }

  setScale(scale: number): void {
    this.setView(<GanttChartView>this.gantt.config.scale_unit, scale);
  }

  private setupViewOptions(mode: GanttChartView, scale: number): void {
    // if we do not have to calculate non-working days => we can avoid rendering of cells to optimize rendering
    const isAllDaysPeriodType = this.state.project(queryGanttIsAllDaysPeriodType);
    this.gantt.config.scale_unit = mode;
    this.timeline.removeClass('gantt_scale_by_month');
    this.timeline.removeClass('gantt_scale_by_week');
    this.timeline.removeClass('gantt_scale_by_day');
    this.timeline.addClass(`gantt_scale_by_${mode}`);

    switch (mode) {
      case GanttChartView.MONTH:
        this.gantt.config.static_background = false;
        this.gantt.config.show_task_cells = true;
        this.gantt.config.select_task = true;
        this.gantt['ignore_time'] = <any>void 0;
        this.gantt.config.date_scale = <any>void 0;
        this.gantt.templates.date_scale = (date: Date) => this.getMonthlyScale(date);
        this.gantt.config.subscales = [
          {
            unit: GanttChartView.DAY,
            step: 1,
            template: dateOfMonthFormatter,
            css: getSubScaleCellClass,
            // task_cell_class: getTaskCellClass,
          },
        ];
        this.gantt.config.min_column_width = 20 * scale;
        this.gantt.config.round_dnd_dates = true;
        this.gantt.config.time_step = 60;
        this.minScale = 1;
        this.maxScale = 3;
        break;
      case GanttChartView.WEEK:
        this.gantt.config.static_background = false;
        this.gantt.config.show_task_cells = true;
        this.gantt.config.select_task = true;
        this.gantt['ignore_time'] = <any>void 0;
        this.gantt.templates.date_scale = (date: Date) => this.getWeeklyScale(date);
        this.gantt.config.subscales = [
          {
            unit: GanttChartView.DAY,
            step: 1,
            template: dateOfMonthFormatter,
            css: getSubScaleCellClass,
            // task_cell_class: getTaskCellClass,
          },
        ];
        this.gantt.config.min_column_width = 55 * scale;
        this.gantt.config.round_dnd_dates = true;
        this.gantt.config.time_step = 60;
        this.minScale = 0.5;
        this.maxScale = 4;
        break;
      case GanttChartView.DAY:
        // it is not necessary to use dynamic background for day view,
        // because we do not show weekend and do not need to color them
        this.gantt.config.static_background = false;
        this.gantt.config.show_task_cells = true;
        this.gantt.config.select_task = true;
        this.gantt['ignore_time'] = (date: Date) => {
          if (date.getHours() < WORK_TIME_HOURS_START || date.getHours() >= WORK_TIME_HOURS_END) {
            return true;
          } else if (!isAllDaysPeriodType && (date.getDay() === 0 || date.getDay() === 6)) {
            return true;
          } else {
            return false;
          }
        };
        this.gantt.config.date_scale = '%F %d';
        this.gantt.templates.date_scale = <any>void 0;
        this.gantt.config.subscales = [
          {
            unit: GanttChartView.HOUR,
            step: 1,
            template: (date: Date) => DateTimeTypes.ShortTime.format(date),
          },
        ];
        this.gantt.config.min_column_width = 65 * scale;
        this.gantt.config.round_dnd_dates = false;
        this.gantt.config.time_step = -1;
        this.minScale = 0.9;
        this.maxScale = 2;
        break;
      default:
        break;
    }
  }

  private setupCellClassTemplate(): void {
    this.gantt.templates.scale_cell_class = getPrimaryScaleCellClass;
    this.gantt.templates.task_cell_class = this.getTaskCellClass.bind(this);
  }

  private getWeeklyScale(date: Date): string {
    const dateToStr = this.gantt.date.date_to_str('%d %M');
    const weekStartDate = this.gantt.date.week_start(new Date(date));
    const weekEndDate = this.gantt.date.add(
      this.gantt.date.add(weekStartDate, 1, GanttChartView.WEEK),
      -1,
      GanttChartView.DAY,
    );

    if (this.isWeekLabelFullyVisibleOnScale(weekStartDate, weekEndDate)) {
      return `${dateToStr(weekStartDate)} - ${dateToStr(weekEndDate)}`;
    } else {
      return '';
    }
  }

  private getMonthlyScale(date: Date): string {
    const dateToStr = this.gantt.date.date_to_str('%F, %Y');
    const monthStartDate = this.gantt.date.month_start(new Date(date));
    const monthEndDate = this.gantt.date.add(
      this.gantt.date.add(monthStartDate, 1, GanttChartView.MONTH),
      -1,
      GanttChartView.DAY,
    );

    if (this.isMonthLabelFullyVisibleOnScale(monthStartDate, monthEndDate)) {
      return dateToStr(date);
    } else {
      return '';
    }
  }

  private getTaskCellClass(item: any, date: Date): string {
    if (-1 !== [0, 6].indexOf(date.getDay())) {
      return 'gantt_cell_weekend';
    } else if (isDayOff(date)) {
      return 'gantt_cell_dayoff';
    } else {
      return '';
    }
  }

  private setDayOff(date: Date): void {
    const byYear = dayOffByYearIndex[date.getFullYear()] || {};
    const byMonth = byYear[date.getMonth()] || {};
    dayOffByYearIndex[date.getFullYear()] = byYear;
    byYear[date.getMonth()] = byMonth;
    byMonth[date.getDate()] = true;
  }

  private isWeekLabelFullyVisibleOnScale(weekStartDate: Date, weekEndDate: Date): boolean {
    const startDate = this.state.project(queryGanttStartVisibleDate);
    const endDate = this.state.project(queryGanttEndVisibleDate);
    return Math.abs(diffDays(startDate, weekEndDate)) > 2 && Math.abs(diffDays(weekStartDate, endDate)) > 2;
  }

  private isMonthLabelFullyVisibleOnScale(monthStartDate: Date, monthEndDate: Date): boolean {
    const startDate = this.state.project(queryGanttStartVisibleDate);
    const endDate = this.state.project(queryGanttEndVisibleDate);
    return Math.abs(diffDays(startDate, monthEndDate)) > 4 && Math.abs(diffDays(monthStartDate, endDate)) > 4;
  }
}
